/**
 * 금액을 자릿수(count)에 따라 '만', '억', '조' 단위로 축약하여 반환합니다.
 *
 * 예)
 *  - 4자리 이하  → "9,999만"
 *  - 5~8자리    → "1억 2,345만"
 *  - 9자리       → "1조 2,345억 6,789만"
 *
 * @param {number} count - price 문자열의 자릿수 (ex. price.length)
 * @param {string} price - 문자열 형태의 금액 (ex. "12345678")
 * @returns {string} 포맷된 금액 문자열 (ex. "1억 2,345만")
 */
export const formatAmountUnit = (count, price) => {
  // 문자열 price -> 숫자 체크
  const n = Number(price);
  if (!Number.isFinite(n) || n === 0) return "0";

  if (count >= 1 && count < 5) {
    // 1만 미만 ~ 9,999만 구간
    return n === 0 ? "0" : `${n.toLocaleString()}만`;
  } else if (count >= 5 && count < 9) {
    // 억 단위 포함 (최대 9,999억 9,999만)
    const high = Number(price.substring(0, count - 4)); // 억
    const low = Number(price.substring(count - 4, price.length)); // 만

    if (high === 0 && low === 0) return "0";

    const highPart = high > 0 ? `${high}억` : "";
    const lowPart = low > 0 ? ` ${low.toLocaleString()}만` : "";
    // 둘 다 0이면 위에서 걸러짐
    return (highPart + lowPart).trim() || "0";
  } else if (count === 9) {
    // 조 단위 (1조 ~ 9조 9,999억 9,999만)
    const jo = Number(price.substring(0, 1));
    const eok = Number(price.substring(1, count - 4));
    const man = Number(price.substring(count - 4, price.length));

    if (jo === 0 && eok === 0 && man === 0) return "0";

    const joPart = jo > 0 ? `${jo}조` : "";
    const eokPart = eok > 0 ? ` ${eok}억` : "";
    const manPart = man > 0 ? ` ${man}만` : "";
    const result = `${joPart}${eokPart}${manPart}`.trim();
    return result || "0";
  }

  // 범위를 벗어나는 경우도 안전하게 처리
  return n === 0 ? "0" : `${n.toLocaleString()}만`;
};

/**
 * 숫자 금액을 10,000 단위(만, 억, 조, 경...)로 분리하여
 * 사람이 읽기 쉬운 한글 화폐 단위 문자열로 변환합니다.
 *
 * 예를 들어 12345678900 → "1조 2345억 6789만 8900"
 *
 * @param {number|string} price - 변환할 금액 (예: 12345678900 또는 "12345678900")
 * @param {Object} [options] - 옵션 객체
 * @param {boolean} [options.withWon=false] - true일 경우 문자열 끝에 "원"을 붙임
 * @param {string[]} [options.unitWords=["", "만", "억", "조", "경"]] - 단위 배열 (확장 가능)
 * @returns {string} 변환된 문자열 (예: "1조 2345억 6789만 8900원" 또는 "0")
 */
export const formatFullAmountUnit = (
  price,
  { withWon = false, unitWords } = {}
) => {
  const units = unitWords ?? ["", "만", "억", "조", "경"];
  const n = Number(String(price).replace(/,/g, ""));
  if (!Number.isFinite(n) || n <= 0) return "0" + (withWon ? "원" : "");

  let rest = Math.floor(n);
  const parts = [];

  // 10,000 단위씩 나눠서 각 단위 붙이기
  for (let i = 0; i < units.length && rest > 0; i++) {
    const unitVal = rest % 10000;
    if (unitVal > 0) parts.unshift(`${unitVal.toLocaleString()}${units[i]}`);
    rest = Math.floor(rest / 10000);
  }

  // 각 단위 사이를 공백으로 구분
  let result = parts.join(" ");

  // '원' 옵션 처리
  if (withWon) {
    // 마지막 단위가 "만"으로 끝나면 → "만원"
    if (result.endsWith("만")) {
      result += "원";
      result = result.replace(/만원$/, "만원");
    } else {
      result += "원";
    }
  }

  return result;
};
